import {
  DEFAULT_INSTANCE_BRANDING,
  type InstanceBranding,
  type PublicInstanceBootstrap,
} from "@/lib/instance-platform";

export const INSTANCE_BRANDING_ATTRIBUTE = "data-instance-branded";

export type InstanceCSSVariables = Record<`--${string}`, string>;

function cssURL(value: string) {
  return `url(${JSON.stringify(value)})`;
}

export function instanceBackgroundValue(branding: InstanceBranding) {
  if (branding.background_image_url) {
    return `linear-gradient(135deg, color-mix(in srgb, ${branding.background_color} 76%, transparent), color-mix(in srgb, ${branding.secondary_color} 38%, transparent)), ${cssURL(branding.background_image_url)}`;
  }
  return `radial-gradient(circle at 12% 8%, color-mix(in srgb, ${branding.primary_color} 22%, transparent), transparent 34%), radial-gradient(circle at 88% 10%, color-mix(in srgb, ${branding.accent_color} 18%, transparent), transparent 30%), linear-gradient(145deg, ${branding.background_color}, color-mix(in srgb, ${branding.background_color} 84%, ${branding.secondary_color}))`;
}

export function instanceBrandingCSSVariables(branding: InstanceBranding): InstanceCSSVariables {
  return {
    "--instance-primary-color": branding.primary_color,
    "--instance-secondary-color": branding.secondary_color,
    "--instance-accent-color": branding.accent_color,
    "--instance-background-color": branding.background_color,
    "--instance-surface-color": branding.surface_color,
    "--instance-text-color": branding.text_color,
    "--instance-muted-text-color": branding.muted_text_color,
    "--instance-font-family": `${branding.font_family || DEFAULT_INSTANCE_BRANDING.font_family}, ui-sans-serif, system-ui, sans-serif`,
    "--instance-background-image": instanceBackgroundValue(branding),
  };
}

export function applyInstanceBranding(
  bootstrap: PublicInstanceBootstrap,
  targetDocument: Document = document,
) {
  const root = targetDocument.documentElement;
  const variables = instanceBrandingCSSVariables(bootstrap.branding);
  for (const [name, value] of Object.entries(variables)) root.style.setProperty(name, value);
  root.setAttribute(INSTANCE_BRANDING_ATTRIBUTE, "true");
  root.setAttribute("data-instance-id", bootstrap.instance_id);
  root.setAttribute("data-instance-slug", bootstrap.slug);
  targetDocument.title = instanceDocumentTitle(bootstrap.display_name);
  updateIcon(targetDocument, bootstrap.branding.favicon_url);
  updateThemeColor(targetDocument, bootstrap.branding.primary_color);
  return variables;
}

export function instanceDocumentTitle(displayName: string) {
  const name = displayName.trim() || "GENRL";
  return /\bwallet$/i.test(name) ? name : `${name} Wallet`;
}

export function clearInstanceBranding(targetDocument: Document = document) {
  const root = targetDocument.documentElement;
  for (const name of Object.keys(instanceBrandingCSSVariables(DEFAULT_INSTANCE_BRANDING))) {
    root.style.removeProperty(name);
  }
  root.removeAttribute(INSTANCE_BRANDING_ATTRIBUTE);
  root.removeAttribute("data-instance-id");
  root.removeAttribute("data-instance-slug");
  targetDocument.querySelector('link[data-instance-favicon="true"]')?.remove();
  targetDocument.querySelector('meta[data-instance-theme-color="true"]')?.remove();
}

function updateIcon(targetDocument: Document, faviconURL: string | undefined) {
  targetDocument.querySelector('link[data-instance-favicon="true"]')?.remove();
  if (!faviconURL) return;
  const link = targetDocument.createElement("link");
  link.rel = "icon";
  link.href = faviconURL;
  link.setAttribute("data-instance-favicon", "true");
  targetDocument.head.appendChild(link);
}

function updateThemeColor(targetDocument: Document, color: string) {
  let meta = targetDocument.querySelector<HTMLMetaElement>(
    'meta[data-instance-theme-color="true"]',
  );
  if (!meta) {
    meta = targetDocument.createElement("meta");
    meta.name = "theme-color";
    meta.setAttribute("data-instance-theme-color", "true");
    targetDocument.head.appendChild(meta);
  }
  meta.content = color;
}
